import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';

/**
 * Shared list-page state: paging, filters, loading and reload.
 * `fetcher` receives the merged query and returns the API envelope.
 */
const useList = (fetcher, initialFilters = {}, { limit = 20, auto = true } = {}) => {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(auto);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);

  const load = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      try {
        const res = await fetcher({ page, limit, ...filters, ...overrides });
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta(res.meta || null);
      } catch (err) {
        toast.error(err.message);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, limit, JSON.stringify(filters)],
  );

  useEffect(() => {
    if (auto) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  /** Changing any filter resets to the first page. */
  const setFilter = useCallback((key, value) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  return { rows, meta, loading, page, setPage, filters, setFilter, setFilters, reload: load };
};

export default useList;
